import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";

import { NormalizedInsight } from "@core/apis/PropAI/types/insights.type";
import { formatDate } from "@core/utils";

import { CheckItemsList } from "./CheckItemsList";
import { INSIGHT_CONFIG } from "./insightConfig";
import { TableRowLayout } from "./TableRowLayout";

type InsightRowProps = Readonly<{
  insight: NormalizedInsight;
}>;

/**
 * Build interpolation values for translation strings based on insight data
 */
function buildValues(insight: NormalizedInsight): Record<string, string | number | undefined> {
  const { meta_data, insight_data, date_start, date_end } = insight;

  return {
    // Date values
    startDate: formatDate(date_start),
    endDate: formatDate(date_end),

    // Sensor/apartment values
    floor: insight_data?.floor ?? "",
    apartment: insight_data?.apartment ?? "",
    zone: insight_data?.zone ?? "",
    streetAddress: insight_data?.street_address ?? "",

    // Numeric values with formatting
    deviation: meta_data.deviation_value?.toFixed(1),
    averageValue: meta_data.average_value?.toFixed(1),
    spreadValue: meta_data.spread_value?.toFixed(1),
    normalSpreadValue: meta_data.normal_spread_value?.toFixed(1),

    // Efficiency values
    efficiencyPercentage: meta_data.efficiency_percentage?.toFixed(1),
    energySavings: meta_data.energy_savings?.toFixed(1),

    // Power peak values
    peakPower: meta_data.peak_power?.toFixed(1),
    city: meta_data.city ?? "",
    comparisonStatus: meta_data.comparison_status ?? "",

    // Energy efficiency values
    energyUsageKwhM2: meta_data.energy_usage_kwh_m2?.toFixed(1),
    percentile: meta_data.percentile?.toString(),
  };
}

/**
 * Build the subtitle for sensor category insights
 * Returns a translation key if floor and apartment are present, otherwise apartment string
 */
function buildSubtitle(
  insight: NormalizedInsight,
  values: Record<string, unknown>
): { key: string; values: Record<string, unknown> } | string | undefined {
  const config = INSIGHT_CONFIG[insight.type];
  if (config.category !== "sensor") {
    return undefined;
  }

  const floor = values.floor;
  const apartment = values.apartment;

  if (floor !== "" && apartment !== "") {
    return { key: "sensor_subtitle_floor_apartment", values: { floor, apartment } };
  }

  return typeof apartment === "string" && apartment !== "" ? apartment : undefined;
}

export function InsightRow({ insight }: InsightRowProps) {
  const { t } = useTranslation(["propai_insights"]);
  const config = INSIGHT_CONFIG[insight.type];

  if (!config) {
    return null;
  }

  const values = buildValues(insight);
  const subtitleResult = buildSubtitle(insight, values);

  // Resolve subtitle: either translate a key or use the raw string
  const floorApartmentSubtitle =
    subtitleResult && typeof subtitleResult === "object"
      ? t(subtitleResult.key, subtitleResult.values as Record<string, string>)
      : subtitleResult;

  const streetAddressSubtitle =
    typeof values.streetAddress === "string" && values.streetAddress !== ""
      ? values.streetAddress
      : undefined;

  const recommendations = (
    <>
      <Typography variant="bodyMedium" paragraph>
        {t(config.reasonIntro)}
      </Typography>
      <CheckItemsList checkItems={config.checkItems} t={t} />
    </>
  );

  return (
    <TableRowLayout
      title={t(config.title)}
      floorApartmentSubtitle={floorApartmentSubtitle}
      streetAddressSubtitle={streetAddressSubtitle}
      description={t(config.description, values)}
      recommendations={recommendations}
    />
  );
}
